import { horizontalRelationships } from '../config/constants';
import type { FamilyTreeEdgeType, FamilyTreeNode } from '../interfaces';
import { NodeHandlerId } from '../types/node-handler-id';
import {
    getOppositeNodeHandle,
    isAnyChildNodeVisible,
    isBottomNodeHandle,
    isConnectorNode,
    isNodeIdInEdge,
    isNodeWithChildren,
    isVisibleEdge,
    isVisibleNode,
} from '../utils';

export function useVisibleNodesAndEdges(nodes: FamilyTreeNode[], edges: FamilyTreeEdgeType[], isPresentationMode: boolean) {
    if (!isPresentationMode) return { visibleNodes: nodes, visibleEdges: edges };

    // Filter nodes so only HelperLine-Nodes and Partner/Connector-Nodes with visibility "VISIBLE" are left
    const visibleNodes = nodes.filter(isVisibleNode);
    // Filter edges so only Edges with at least 1 visible connected node are left
    const visibleEdges = edges.filter(isVisibleEdge(visibleNodes));
    // Get all connector nodes that are not visible
    const hiddenConnectorNodes = nodes.filter((node) => isConnectorNode(node) && !isVisibleNode(node));

    const parentNodesForUpdate: { id: string; oldHandle: NodeHandlerId; hasVisibleChildren: boolean }[] = [];

    const newVisibleEdges = hiddenConnectorNodes.reduce(
        (accEdges, connectorNode) => {
            // Get all edges that are connected to the connector node
            const connectedEdges = visibleEdges.filter((edge) => [edge.source, edge.target].includes(connectorNode.id));

            const emptyConnectors: { connectedParent?: FamilyTreeNode; connectedChildren?: (FamilyTreeNode & { justification?: string })[] } = {};

            // Get the still-visible parent and children of the connected nodes
            const { connectedParent, connectedChildren } = visibleNodes.reduce((acc, node) => {
                const connectedEdge = connectedEdges.find((edge) => isNodeIdInEdge(node.id)(edge));

                if (connectedEdge?.data) {
                    if (connectedEdge.data.relationshipType === 'IS_PARENT_OF') {
                        return { ...acc, connectedChildren: [...(acc.connectedChildren ?? []), { ...node, justification: connectedEdge.data.justification }] };
                    } else if (horizontalRelationships.includes(connectedEdge.data.relationshipType)) {
                        parentNodesForUpdate.push({
                            id: node.id,
                            oldHandle: getOppositeNodeHandle(
                                (connectedEdge.source === connectorNode.id ? connectedEdge.sourceHandle : connectedEdge.targetHandle) as NodeHandlerId,
                            ),
                            hasVisibleChildren: isNodeWithChildren(connectorNode) && isAnyChildNodeVisible(connectorNode, edges, nodes),
                        });

                        return { ...acc, connectedParent: node };
                    }
                }

                return acc;
            }, emptyConnectors);

            // Remove the old connected edges that are connected through the now hidden connector node
            const filteredEdges = accEdges.filter((edge) => !connectedEdges.includes(edge));

            // Add a new direct edge if visible parent and children are given and prepared
            if (connectedParent && connectedChildren?.length) {
                connectedChildren.forEach(({ justification, ...connectedChild }) => {
                    filteredEdges.push({
                        id: `${connectedParent.id}-${connectedChild.id}`,
                        source: connectedParent.id,
                        sourceHandle: NodeHandlerId.SOURCE_BOTTOM,
                        target: connectedChild.id,
                        targetHandle: NodeHandlerId.TARGET_TOP,
                        type: 'familyTreeEdge',
                        data: { relationshipType: 'IS_PARENT_OF', justification },
                    });
                });
            }

            return filteredEdges;
        },
        [...visibleEdges],
    );

    const newVisibleNodes = visibleNodes
        // Update the parent nodes with the new handles that connect them directly to the children
        .map((node) => {
            const updatedNodes = parentNodesForUpdate.filter((n) => n.id === node.id);

            if (updatedNodes.length) {
                const handlesToRemove = updatedNodes.map((n) => n.oldHandle);
                const usedHandles: NodeHandlerId[] = [
                    ...node.data.usedHandles.filter((handle) => !handlesToRemove.includes(handle)),
                    ...(updatedNodes.some((n) => n.hasVisibleChildren) ? [NodeHandlerId.SOURCE_BOTTOM] : []),
                ];

                return { ...node, data: { ...node.data, usedHandles } };
            }
            if (isNodeWithChildren(node) && !isAnyChildNodeVisible(node, edges, nodes)) {
                return { ...node, data: { ...node.data, usedHandles: node.data.usedHandles.filter((handle) => !isBottomNodeHandle(handle)) } };
            }

            return node;
        });

    return { visibleNodes: newVisibleNodes, visibleEdges: newVisibleEdges };
}
